import { api, state, ui } from "../../interfaces/interfaces";
import { logger } from "@services/workers/helpers";
import env_config from "../config";
import CancellationTokenStore, { CancellationToken } from "@services/tokens";
import { cacheUtils } from "../..";
import { CacheTypes } from "../cache/interfaces";
import { Card, Payments, Square, StoreVerifyBuyerDetails } from "@square/web-payments-sdk-types";
import { dispatchResetSquareUI } from "@reducers/uiReducer";
import { dispatchResetSquareState } from "@reducers/stateReducer";
import config from "../config";
import AddressUtils from "../address";

declare global {
  interface Window {
    SqPaymentForm: any;
  }
}

interface SquareResponse {
  details: {
    card: {
      brand: string;
      expMonth: number;
      expYear: number;
      last4: string;
    };
    method: string;
    billing: {
      postalCode: string;
    };
  };
  status: string;
  token: string;
}

const namespace = "SQUARE Payments";

const darkModeCardStyle = {
  ".input-container": {
    // borderColor: '#2D2D2D',
    borderRadius: "6px",
    // width: "100px"
  },
  ".input-container.is-focus": {
    // borderColor: '#006AFF',
  },
  ".input-container.is-error": {
    // borderColor: '#ff1600',
  },
  ".message-text": {
    // color: '#999999',
  },
  ".message-icon": {
    // color: '#999999',
  },
  ".message-text.is-error": {
    // color: '#ff1600',
  },
  ".message-icon.is-error": {
    // color: '#ff1600',
  },
  input: {
    // backgroundColor: '#2D2D2D',
    // color: '#FFFFFF',
    fontFamily: "helvetica neue, sans-serif",
  },
  "input::placeholder": {
    // color: '#999999',
  },
  "input.is-error": {
    // color: '#ff1600',
  },
  "@media screen and (max-width: 600px)": {
    input: {
      fontSize: "12px",
    },
  },
};

type ReturnObject = ({
  jobId,
  applicationId,
  locationId,
  nonce,
  verification,
  userChallenged,
  last4,
  postalCode,
}: {
  jobId: string;
  applicationId: string;
  locationId: string;
  nonce: string;
  verification: string;
  userChallenged: string;
  last4: string;
  postalCode: string;
}) => {
  LinkTableName: "Jobs";
  LinkFieldName: "JobId";
  LinkId: string;
  ApplicationId: typeof applicationId;
  LocationId: typeof locationId;
  CardNonce: typeof nonce;
  CardLast4: typeof last4;
  CardPostcode: typeof postalCode;
  CardVerification: typeof verification;
  UserChallenged: typeof userChallenged;
  DebugInfo: typeof userChallenged;
};

class SquareWebSDKAPI {
  private _isLoaded: boolean;
  private _isLoading: boolean;
  private _namespace: string;
  private _script: any;
  private _squareClass: Square | undefined;
  private _squareScript: any;
  private _billingContact: StoreVerifyBuyerDetails["billingContact"];
  private _applicationId: string;
  private _locationId: string;
  private _accessToken: string;
  private _jobId: string;
  private _verification: string;
  private _userChallenged: string;
  private _response: any;
  private _postcode: string;
  private _card: Card | undefined;
  private _src: string;
  public paymentForm: Payments | undefined;
  //   constructor(ui: TemplateUIObject) {
  constructor() {
    this._isLoaded = false;
    this._isLoading = false;
    this._namespace = "SquareAPI";
    this._script = null;
    this._squareClass = undefined;
    this._squareScript = null;
    this._applicationId = "";
    this._locationId = "";
    this._accessToken = "";
    this._jobId = "";
    this._verification = "";
    this._userChallenged = "";
    this._response = "";
    this._card = undefined;
    this.paymentForm = undefined;
    this._billingContact = {
      familyName: "",
      givenName: "",
      email: "",
      city: "",
      addressLines: [],
      postalCode: this.postcode,
      countryCode: "GB",
    };
    this._postcode = "";
    this._src = env_config.debug
      ? "https://sandbox.web.squarecdn.com/v1/square.js"
      : "https://web.squarecdn.com/v1/square.js";
  }

  /**
   * Getter src
   * @return {string}
   */
  public get src(): string {
    return this._src;
  }

  /**
   * Setter src
   * @param {string} value
   */
  public set src(value: string) {
    this._src = value;
  }
  /**
   * Getter src
   * @return {Card | undefined}
   */
  public get card(): Card | undefined {
    return this._card;
  }

  /**
   * Setter src
   * @param {Card | undefined} value
   */
  public set card(value: Card | undefined) {
    this._card = value;
  }

  /**
   * Getter postcode
   * @return {string}
   */
  public get postcode(): string {
    return this._postcode;
  }

  /**
   * Setter postcode
   * @param {string} value
   */
  public set postcode(value: string) {
    this._postcode = value;
  }

  /**
   * Getter billingContact
   * @return {StoreVerifyBuyerDetails["billingContact"]}
   */
  public get billingContact(): StoreVerifyBuyerDetails["billingContact"] {
    return this._billingContact;
  }

  /**
   * Setter billingContact
   * @param {StoreVerifyBuyerDetails["billingContact"]} value
   */
  public set billingContact(value: StoreVerifyBuyerDetails["billingContact"]) {
    this._billingContact = value;
  }

  /**
   * Getter jobId
   * @return {string}
   */
  public get jobId(): string {
    return this._jobId;
  }

  /**
   * Setter jobId
   * @param {string} value
   */
  public set jobId(value: string) {
    this._jobId = value;
  }

  /**
   * Getter isLoaded
   * @return {boolean}
   */
  public get isLoaded(): boolean {
    return this._isLoaded;
  }
  /**
   * Getter isLoading
   * @return {boolean}
   */
  public get isLoading(): boolean {
    return this._isLoading;
  }

  /**
   * Getter namespace
   * @return {string}
   */
  public get namespace(): string {
    return this._namespace;
  }

  /**
   * Getter script
   * @return {any}
   */
  public get script(): any {
    return this._script;
  }

  /**
   * Getter squareClass
   * @return {any}
   */
  public get squareClass(): Square | undefined {
    return this._squareClass;
  }

  /**
   * Getter squareScript
   * @return {any}
   */
  public get squareScript(): any {
    return this._squareScript;
  }

  /**
   * Getter applicationId
   * @return {string}
   */
  public get applicationId(): string {
    return this._applicationId;
  }

  /**
   * Getter locationId
   * @return {string}
   */
  public get locationId(): string {
    return this._locationId;
  }

  /**
   * Getter accessToken
   * @return {string}
   */
  public get accessToken(): string {
    return this._accessToken;
  }

  /**
   * Getter verification
   * @return {string}
   */
  public get verification(): string {
    return this._verification;
  }

  /**
   * Getter userChallenged
   * @return {string}
   */
  public get userChallenged(): string {
    return this._userChallenged;
  }

  /**
   * Getter response
   * @return {any}
   */
  public get response(): any {
    return this._response;
  }

  /**
   * Setter isLoaded
   * @param {boolean} value
   */
  public set isLoaded(value: boolean) {
    this._isLoaded = value;
  }

  /**
   * Setter isLoading
   * @param {boolean} value
   */
  public set isLoading(value: boolean) {
    this._isLoading = value;
  }

  /**
   * Setter namespace
   * @param {string} value
   */
  public set namespace(value: string) {
    this._namespace = value;
  }

  /**
   * Setter script
   * @param {any} value
   */
  public set script(value: any) {
    this._script = value;
  }

  /**
   * Setter squareClass
   * @param {any} value
   */
  public set squareClass(value: Square | undefined) {
    this._squareClass = value;
  }

  /**
   * Setter squareScript
   * @param {any} value
   */
  public set squareScript(value: any) {
    this._squareScript = value;
  }

  /**
   * Setter applicationId
   * @param {string} value
   */
  public set applicationId(value: string) {
    this._applicationId = value;
  }

  /**
   * Setter locationId
   * @param {string} value
   */
  public set locationId(value: string) {
    this._locationId = value;
  }

  /**
   * Setter accessToken
   * @param {string} value
   */
  public set accessToken(value: string) {
    this._accessToken = value;
  }

  /**
   * Setter verification
   * @param {string} value
   */
  public set verification(value: string) {
    this._verification = value;
  }

  /**
   * Setter userChallenged
   * @param {string} value
   */
  public set userChallenged(value: string) {
    this._userChallenged = value;
  }

  /**
   * Setter response
   * @param {any} value
   */
  public set response(value: any) {
    this._response = value;
  }

  async getLocationId() {
    try {
      const response = await api
        .fetchSquareLocationId(env_config.debug ? "TEST" : "LIVE")
      if (response) {
        this.locationId = response;
        logger.debug(this.namespace, `Location Id fetched ${response}`);
      }
    } catch (error) {
      logger.error(this.namespace, "Location Id fetch failed.", error);
    }
  }

  async getApplicationId() {
    const appId = cacheUtils
      .getCache<CacheTypes.GlobalSettings>("GlobalSettings")
      .find(
        (c) => c.setting === `Square.ApplicationID.RightioSubcontractor.${env_config.debug ? "TEST" : "LIVE"}`
      )?.settingValue;
    if (appId) {
      this.applicationId = appId;
      return;
    }
  }

  get verificationDetails(): StoreVerifyBuyerDetails {
    return {
      intent: "STORE", //Allowed values: "CHARGE", "STORE"
      billingContact: {
        familyName: this.billingContact.familyName,
        givenName: this.billingContact.givenName,
        email: this.billingContact.email,
        countryCode: this.billingContact.countryCode,
        city: this.billingContact.city,
        addressLines: this.billingContact.addressLines,
        postalCode: this.billingContact.postalCode,
      },
    };
  }
  returnObj: ReturnObject = ({
    jobId,
    applicationId,
    locationId,
    nonce,
    verification,
    userChallenged,
    last4,
    postalCode,
  }) => ({
    LinkTableName: "Jobs",
    LinkFieldName: "JobId",
    LinkId: jobId,
    ApplicationId: applicationId,
    LocationId: locationId,
    CardNonce: nonce,
    CardLast4: last4,
    CardPostcode: postalCode,
    CardVerification: verification,
    UserChallenged: userChallenged,
    DebugInfo: "User challenged = " + userChallenged,
  });

  async verifyBuyer() {
    ui.SquareInfo = "PROCESSING... Verification begin";
    try {
      // BELOW IS THE CODE FOR VERIFYING THE BUYER BUT THIS IS INCREASING MOTD RATE AND IS NOT USED
      const details = await this.paymentForm?.verifyBuyer(ui.SquareNonce, this.verificationDetails);
      ui.SquareVerification = details?.token;
      ui.SquareUserChallenged = details?.userChallenged;
      ui.SquareSucceeded = true;
    } catch (typeError: any) {
      //TypeError thrown if illegal arguments are passed
      logger.error(this.namespace, "Buyer verification failed.", typeError);
      ui.SquareError = JSON.stringify(typeError);
      alert(typeError);
      ui.SquareError = "Buyer verification failed.";
      ui.SquareResetEnabled = true;
      ui.SquareSucceeded = false;
    }
  }
  async verifyBuyerSkip() {
    ui.SquareInfo = "PROCESSING... Verification begin";
    try {
      // BELOW IS THE CODE FOR VERIFYING THE BUYER BUT THIS IS INCREASING MOTD RATE AND IS NOT USED
      ui.SquareSucceeded = true;
    } catch (typeError: any) {
      //TypeError thrown if illegal arguments are passed
      logger.error(this.namespace, "Buyer verification failed.", typeError);
      ui.SquareError = JSON.stringify(typeError);
      alert(typeError);
    }
  }
  async tokenize() {
    ui.SquareProcessing = true;
    ui.SquareInfo = "PROCESSING... Tokenization begin";
    try {
      logger.debug(this.namespace, "Tokenization begin");
      const result = (await this.card?.tokenize()) as SquareResponse;

      // console.log(result)

      // check if postcode was not modified
      if (result?.details?.billing?.postalCode !== ui.BookingPaymentAddressPostcode) {
        ui.SquareError = "Postcode was modified, please verify postcode and try again.";
        ui.SquareProcessing = false;
        ui.SquareResetEnabled = true;
        return;
      }

      if (result.status === "OK") {
        await this.cardNonceResponseReceived(result.token, result.details.card);
        ui.SquareCardString = `Card details: Brand - ${result.details.card.brand} | Last 4 - ${result.details.card.last4} | Expiry - ${result.details.card.expMonth}/${result.details.card.expYear}`
      }
    } catch (e) {
      console.error(e);
      logger.error(this.namespace, "Tokenization failed.", JSON.parse(JSON.stringify(e)));
    }
    finally {
      ui.SquareProcessing = false;
    }
  }
  paymentFormLoaded() {
    return () => {
      if (document !== null) {
        const a = document.getElementById("sq-card-number");
        const b = document.getElementById("sq-expiration-date");
        const c = document.getElementById("sq-cvv");
        const d = document.getElementById("pay-btn");
        if (a) a.style.maxWidth = "165px";
        if (b) b.style.maxWidth = "75px";
        if (c) c.style.maxWidth = "75px";
        if (d) d.hidden = false;
      }

      ui.SquareFormLoaded = true;
      // this.setPostalCode();
    };
  }
  async cardNonceResponseReceived(nonce: string, cardData: any) {
    ui.SquareInfo = "PROCESSING... Card response received.";
    ui.SquareCardData = cardData;
    ui.SquareNonce = nonce;
    if (nonce) {
      // Assign the nonce value to the hidden form field
      logger.debug(this.namespace, `Nonce received: ${nonce}`);
      logger.debug(this.namespace, "Card data received.", cardData);
      if (ui.SquareEnableVerifyBuyer || config.debug) await this.verifyBuyer();
      else await this.verifyBuyerSkip();
    } else {
      logger.error(this.namespace, "Nonce not received.");
      ui.SquareError = "Nonce not received.";
      ui.SquareSucceeded = false;
      ui.SquareResetEnabled = true;
    }
  }
  configure() {
    this.paymentForm = this.squareClass && this.squareClass.payments(this.applicationId, this.locationId);
  }
  build(cancelToken: CancellationToken) {
    if (cancelToken.isCancelled) {
      logger.debug(this.namespace, "Build cancelled.");
      this.cleanUp();
      return;
    }
    // this.paymentForm.build();
  }
  async loadScript() {
    return new Promise<void>((resolve, reject) => {
      this.squareClass = window.Square;
      resolve();
    });
  }
  async fetchSquare() {
    const cancellationTokenStore = new CancellationTokenStore();
    const cancelToken = cancellationTokenStore.generateToken();
    ui.Loading = true;
    await this.start(cancelToken);
    console.log("Fetching Square...", this);
    if (this && this.paymentForm) {
      if (this.isLoading) {
        // await this.retry();
        return;
      }
      this.isLoading = true;
      this.paymentForm.setLocale("en-GB");
      this.card = await this.paymentForm.card({
        postalCode: ui.BookingPaymentAddressPostcode,
        includeInputLabels: false,

        style: darkModeCardStyle,
      });
      this.card.attach("#form-container");
      console.log("Fetched Square...", this.card);
      this.postcode = ui.BookingPaymentAddressPostcode;
      this.jobId = String(state.JobId);
      const addressObj = AddressUtils.convertAddressFormFieldsToAddress({ scope: "BookingPaymentAddress", ui })?.addressObj;
      const addressLines = [addressObj?.line1 || "", addressObj?.line2 || "", addressObj?.line3 || ""].filter(Boolean);
      if (addressLines.length === 0) throw new Error("No valid address lines found.");
      this.billingContact = {
        addressLines,
        city: ui.BookingPaymentAddressCity,
        countryCode: "GB",
        email: ui.BookingPaymentContactEmail,
        familyName: ui.BookingPaymentContactLastName,
        givenName: ui.BookingPaymentContactFirstName,
        postalCode: ui.BookingPaymentAddressPostcode,
      };
    }
    console.log("Square loaded", this.squareClass);
    ui.SquareFormLoaded = true;
    ui.Loading = false;

    this.card?.addEventListener("errorClassAdded", this.errorClassAdded);
    this.card?.addEventListener("errorClassRemoved", this.errorClassRemoved);
    this.isLoading = false;
  }
  errorClassAdded() {
    return () => {
      ui.SquareError = "There is a problem with card details. Please check and try again.";
    };
  }
  errorClassRemoved() {
    return () => {
      ui.SquareError = "";
    };
  }

  cleanUp() {
    logger.debug(this.namespace, "CLEAN UP");
    this.card?.removeEventListener("errorClassAdded", this.errorClassAdded);
    this.card?.removeEventListener("errorClassRemoved", this.errorClassRemoved);
    this.card?.detach();
    this.card?.destroy();
    this.card = undefined;
    this.squareClass && delete this.squareClass && (this.squareClass = undefined);
    window.SqPaymentForm && delete window.SqPaymentForm;
    try {
      this.script && this.script?.parentNode?.removeChild(this.script);
    } catch (err: any) {
      logger.error(this.namespace, "Error during cleanup.", err);
    }
    this.paymentForm = undefined;
    dispatchResetSquareUI();
    dispatchResetSquareState();
  }
  async retry() {
    logger.debug(this.namespace, "RESET");
    this.cleanUp();
    setTimeout(async () => {
      await this.fetchSquare();
    }, 1000);
  }
  async start(cancelToken: CancellationToken) {
    logger.debug(namespace, "START UP");
    if (!this.applicationId) await this.getApplicationId();
    if (!this.locationId) await this.getLocationId();
    if (!this.squareClass && cancelToken.isCancelled === false) {
      await this.loadScript();
      this.configure();
    }
  }
}

const squareWebSDKAPI = new SquareWebSDKAPI();

export default squareWebSDKAPI;
